import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, provide, watch } from 'vue';

import { useCalendarDayParams } from './calendar-day-params';
import { createCalendarState } from './calendar-state';
import * as math from './utils';

import type { Props as HeaderProps } from './CalendarHeader.vue';
import Item from './CalendarItem.vue';
import Day, { type Props as DayProps } from './CalendarDay.vue';

import type {
  CalendarItem,
  NormalizedCalendarItem,
  DateTimeFormatOption,
  DateClasses,
  PeriodRange,
  PeriodChangedCallback,
  // CalendarProps,
} from './calendar-state';

export interface Props {
  // eslint-disable-next-line vue/require-default-prop
  showDate?: Date;
  // eslint-disable-next-line vue/require-default-prop
  locale?: string;
  displayPeriodUom?: string;
  displayPeriodCount?: number;
  monthNameFormat?: DateTimeFormatOption;
  weekdayNameFormat?: DateTimeFormatOption;
  startingDayOfWeek?: number;
  items?: CalendarItem[];
  dateClasses?: DateClasses;
  currentPeriodLabel?: string;
  currentPeriodLabelIcons?: string;
  monthNameOn1st?: boolean;
  displayWeekNumbers?: boolean;
  showTimes?: boolean;
  timeFormatOptions?: object;
  disablePast?: boolean;
  disableFuture?: boolean;
  enableDateSelection?: boolean;
  enableDragDrop?: boolean;
  enableTooltip?: boolean;
  enableHtmlTitles?: boolean;
  // eslint-disable-next-line vue/require-default-prop
  selectionStart?: Date;
  // eslint-disable-next-line vue/require-default-prop
  selectionEnd?: Date;
  // eslint-disable-next-line vue/require-default-prop
  periodChangedCallback?: PeriodChangedCallback;
  itemTop?: string;
  itemContentHeight?: string;
  itemBorderHeight?: string;
  doEmitItemMouseEvents?: boolean;
}

export interface Emits {
  (
    event: 'period-changed',
    periodRange: PeriodRange,
    source: 'watch' | 'user',
  ): void;
  (
    event: 'click-date',
    day: Date,
    itemsInRange: NormalizedCalendarItem[],
    windowEvent: Event,
  ): void;
  (
    event:
      | 'click-item'
      | 'item-mouseenter'
      | 'item-mouseleave'
      | 'drag-start'
      | 'resize-start',
    item: NormalizedCalendarItem,
    windowEvent: Event,
  ): void;
  (
    event:
      | 'drag-over-date'
      | 'drag-enter-date'
      | 'drag-leave-date'
      | 'drop-on-date',
    currentDragItem: NormalizedCalendarItem,
    day: Date,
    windowEvent: Event,
  ): void;
  (
    event: 'date-selection' | 'date-selection-start' | 'date-selection-finish',
    selectedDateRange: [Date, Date],
    windowEvent: Event,
  ): void;
}


/**
 * See original component source at
 * {@link https://github.com/richardtallent/vue-simple-calendar}
 */

export type {
  CalendarItem,
  NormalizedCalendarItem,
  DateTimeFormatOption,
} from './calendar-state';

const __default__ = { name: 'Calendar' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    showDate: null,
    locale: null,
    displayPeriodUom: { default: 'month' },
    displayPeriodCount: { default: 1 },
    monthNameFormat: { default: 'long' },
    weekdayNameFormat: { default: 'short' },
    startingDayOfWeek: { default: 0 },
    items: { default: () => [] },
    dateClasses: { default: () => ({}) },
    currentPeriodLabel: { default: '' },
    currentPeriodLabelIcons: { default: '⇤-⇥' },
    monthNameOn1st: { type: Boolean, default: true },
    displayWeekNumbers: { type: Boolean, default: false },
    showTimes: { type: Boolean, default: false },
    timeFormatOptions: { default: () => ({}) },
    disablePast: { type: Boolean, default: false },
    disableFuture: { type: Boolean, default: false },
    enableDateSelection: { type: Boolean, default: false },
    enableDragDrop: { type: Boolean, default: false },
    enableTooltip: { type: Boolean, default: true },
    enableHtmlTitles: { type: Boolean, default: true },
    selectionStart: null,
    selectionEnd: null,
    periodChangedCallback: null,
    itemTop: { default: '1.4em' },
    itemContentHeight: { default: '1.4em' },
    itemBorderHeight: { default: '2px' },
    doEmitItemMouseEvents: { type: Boolean, default: false }
  },
  emits: ["period-changed", "click-date", "click-item", "item-mouseenter", "item-mouseleave", "drag-start", "resize-start", "drag-over-date", "drag-enter-date", "drag-leave-date", "drop-on-date", "date-selection", "date-selection-start", "date-selection-finish"],
  setup(__props: any, { expose, emit }: { emit: ({
  (
    event: 'period-changed',
    periodRange: PeriodRange,
    source: 'watch' | 'user',
  ): void;
  (
    event: 'click-date',
    day: Date,
    itemsInRange: NormalizedCalendarItem[],
    windowEvent: Event,
  ): void;
  (
    event:
      | 'click-item'
      | 'item-mouseenter'
      | 'item-mouseleave'
      | 'drag-start'
      | 'resize-start',
    item: NormalizedCalendarItem,
    windowEvent: Event,
  ): void;
  (
    event:
      | 'drag-over-date'
      | 'drag-enter-date'
      | 'drag-leave-date'
      | 'drop-on-date',
    currentDragItem: NormalizedCalendarItem,
    day: Date,
    windowEvent: Event,
  ): void;
  (
    event: 'date-selection' | 'date-selection-start' | 'date-selection-finish',
    selectedDateRange: [Date, Date],
    windowEvent: Event,
  ): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { showDate?: Date, locale?: string, displayPeriodUom: string, displayPeriodCount: number, monthNameFormat: DateTimeFormatOption, weekdayNameFormat: DateTimeFormatOption, startingDayOfWeek: number, items: CalendarItem[], dateClasses: DateClasses, currentPeriodLabel: string, currentPeriodLabelIcons: string, monthNameOn1st: boolean, displayWeekNumbers: boolean, showTimes: boolean, timeFormatOptions: object, disablePast: boolean, disableFuture: boolean, enableDateSelection: boolean, enableDragDrop: boolean, enableTooltip: boolean, enableHtmlTitles: boolean, selectionStart?: Date, selectionEnd?: Date, periodChangedCallback?: PeriodChangedCallback, itemTop: string, itemContentHeight: string, itemBorderHeight: string, doEmitItemMouseEvents: boolean };





const calendar = createCalendarState(props);

provide('calendarState', calendar);

const {
  currentDragItem,
  dateSelectionOrigin,
  displayLocale,
  periodStart,
  periodEnd,
  periodStartCalendarWeek,
  displayFirstDate,
  displayLastDate,
  weeksOfPeriod,
  monthNames,
  weekdayNames,
  fixedItems,
  currentPeriodStart,
  periodLabel,
  currentPeriodLabelFinal,
} = calendar;

const state = reactive({ ...calendar });

const headerProps = computed(() => {
  return {
    // Dates for UI navigation
    previousYear: getIncrementedPeriod(-12),
    previousPeriod: getIncrementedPeriod(-1),
    nextPeriod: getIncrementedPeriod(1),
    previousFullPeriod: getIncrementedPeriod(-props.displayPeriodCount),
    nextFullPeriod: getIncrementedPeriod(props.displayPeriodCount),
    nextYear: getIncrementedPeriod(12),
    currentPeriod: currentPeriodStart.value,
    currentPeriodLabel: currentPeriodLabelFinal.value,
    // Dates for header display
    periodStart: periodStart.value,
    periodEnd: periodEnd.value,
    // Extra information that could be useful to a custom header
    displayLocale: displayLocale.value,
    displayFirstDate: displayFirstDate.value,
    displayLastDate: displayLastDate.value,
    monthNames: monthNames.value,
    fixedItems: fixedItems.value,
    periodLabel: periodLabel.value,
  } as HeaderProps;
});

const periodRange = computed(() => ({
  periodStart: periodStart.value,
  periodEnd: periodEnd.value,
  displayFirstDate: displayFirstDate.value,
  displayLastDate: displayLastDate.value,
}));

const classList = computed(() => {
  const items = [
    `locale-${math.languageCode(displayLocale.value)}`,
    `locale-${displayLocale.value}`,
    `y${periodStart.value.getFullYear()}`,
    `m${math.paddedMonth(periodStart.value)}`,
    `period-${props.displayPeriodUom}`,
    `period-count-${props.displayPeriodCount}`,
  ];

  if (math.isPastMonth(periodStart.value)) {
    items.push('past');
  }

  if (math.isFutureMonth(periodStart.value)) {
    items.push('future');
  }

  if (!math.supportsIntl) {
    items.push('noIntl');
  }

  return items;
});

const periodRangeKey = computed(() => {
  return `${periodStart.value.toISOString()}-${periodEnd.value.toISOString()}`;
});

watch(
  periodRange,
  (newVal) => {
    emit('period-changed', newVal, 'watch');
    props.periodChangedCallback?.(newVal, 'watch');
  },
  { immediate: true, deep: true },
);

//#region UI Events

function onClickDay(day: Date, windowEvent: Event) {
  if (props.disablePast && math.isInPast(day)) return;
  if (props.disableFuture && math.isInFuture(day)) return;

  emit('click-date', day, findAndSortItemsInDateRange(day, day), windowEvent);
}

function onClickItem(calendarItem: NormalizedCalendarItem, windowEvent: Event) {
  emit('click-item', calendarItem, windowEvent);
}

function getDOW(dayIndex: number) {
  return (dayIndex + props.startingDayOfWeek) % 7;
}

/**
 * The day name header needs to know the dow for class assignment, and this
 * value should not change based on startingDayOfWeek (i.e., Sunday is always
 * 0). This function computes the dow for a given day index.
 *
 * @param dayIndex ...
 * @returns ...
 */
function getColumnDOWClass(dayIndex: number) {
  return 'dow' + getDOW(dayIndex).toString();
}

//#endregion UI Events

//#region Date Periods

/**
 * Returns a date for the current display date moved forward or backward by a
 * given number of the current display units. Returns null if said move would
 * result in a disallowed display period.
 */
function getIncrementedPeriod(count: number) {
  const newStartDate = math.incrementPeriod(
    periodStart.value,
    props.displayPeriodUom,
    count,
  );

  const newEndDate = math.incrementPeriod(
    newStartDate,
    props.displayPeriodUom,
    props.displayPeriodCount,
  );

  if (props.disablePast && newEndDate <= math.today()) {
    return null;
  }

  if (props.disableFuture && newStartDate > math.today()) {
    return null;
  }

  return newStartDate;
}

//#endregion Date Periods

//#region Hover Items

function onMouseEnterItem(
  calendarItem: NormalizedCalendarItem,
  windowEvent: Event,
) {
  state.currentHoveredItemId = calendarItem.id;

  if (props.doEmitItemMouseEvents) {
    emit('item-mouseenter', calendarItem, windowEvent);
  }
}

function onMouseLeaveItem(
  calendarItem: NormalizedCalendarItem,
  windowEvent: Event,
) {
  state.currentHoveredItemId = '';

  if (props.doEmitItemMouseEvents) {
    emit('item-mouseleave', calendarItem, windowEvent);
  }
}

//#endregion Hover Items

//#region Dragging Across Days

function onDragDateStart(day: Date, windowEvent: DragEvent) {
  if (!props.enableDateSelection) return false;

  // Push the date where the selection started into dataTransfer. This is not
  // used by this component, but a value required in Firefox and possibly other
  // browsers.
  windowEvent.dataTransfer?.setData('text', day.toString());

  const img = new Image();
  img.src =
    'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

  windowEvent.dataTransfer?.setDragImage(img, 10, 10);

  state.dateSelectionOrigin = day;

  emit('date-selection-start', getSelectedDateRange(day), windowEvent);

  return true;
}

//#endregion Dragging Across Days

//#region Drag and Drop Items

function onDragItemStart(
  calendarItem: NormalizedCalendarItem,
  windowEvent: DragEvent,
) {
  if (!props.enableDragDrop) return false;
  // Firefox and possibly other browsers require dataTransfer to be set, even if
  // the value is not used. IE11 requires that the first argument be exactly
  // "text" (not "text/plain", etc.). The calendar item's ID is passed, allowing
  // calling applications to receive items dragged outside the component.
  windowEvent.dataTransfer?.setData('text', calendarItem.id);

  // However, we don't use dataTransfer within the component. Instead, we just
  // keep a handled on the item currently being dragged. This avoids having to
  // look it up later.
  currentDragItem.value = calendarItem;

  // Reset date selection origin so the onenter events aren't confused
  dateSelectionOrigin.value = null;

  // Allow the calling application to add additional functionality.
  emit('drag-start', calendarItem, windowEvent);

  return true;
}

//#endregion Drag and Drop Items

function onResizeItemStart(
  calendarItem: NormalizedCalendarItem,
  windowEvent: DragEvent,
) {
  console.log('onResizeItemStart', calendarItem);

  // Allow the calling application to add additional functionality.
  emit('resize-start', calendarItem, windowEvent);

  return true;
}

// If the user drags an item FROM this calendar TO this calendar,
// currentDragItem will be initialized to the most recent item with a dragStart
// event. If not, we still emit the event, and the caller will need to determine
// what to do based on the third argument (windowEvent, which gives them access
// to `dataTransfer`). This allows developers to create custom calendars where
// things can be dragged in from the outside. This also allows developers using
// scoped slots for items to handle the drag and drop themselves.

function getSelectedDateRange(date: Date) {
  let value: [Date, Date];

  if (date <= (dateSelectionOrigin.value as Date)) {
    value = [date, dateSelectionOrigin.value as Date];
  } else {
    value = [dateSelectionOrigin.value as Date, date];
  }

  return value;
}

function onDragOver(day: Date, windowEvent: Event) {
  if (!props.enableDragDrop) return;

  emit(
    'drag-over-date',
    state.currentDragItem as NormalizedCalendarItem,
    day,
    windowEvent,
  );
}

function onDragEnter(day: Date, windowEvent: Event) {
  if (props.enableDateSelection && state.dateSelectionOrigin) {
    // User is selecting dates, not items.
    emit('date-selection', getSelectedDateRange(day), windowEvent);
  }

  if (!props.enableDragDrop) return;

  emit(
    'drag-enter-date',
    state.currentDragItem as NormalizedCalendarItem,
    day,
    windowEvent,
  );

  const el = windowEvent.target as HTMLElement;

  el.classList.add('draghover');
}

function onDragLeave(day: Date, windowEvent: Event) {
  // User is selecting dates, not items. No emit.
  if (props.enableDateSelection && props.selectionStart) return;

  if (!props.enableDragDrop) return;

  emit(
    'drag-leave-date',
    state.currentDragItem as NormalizedCalendarItem,
    day,
    windowEvent,
  );

  const el = windowEvent.target as HTMLElement;

  el.classList.remove('draghover');
}

function onDrop(day: Date, windowEvent: Event): void {
  if (props.enableDateSelection && state.dateSelectionOrigin) {
    // User is selecting dates, not items.
    emit('date-selection-finish', getSelectedDateRange(day), windowEvent);

    return;
  }

  if (!props.enableDragDrop) return;

  emit(
    'drop-on-date',
    state.currentDragItem as NormalizedCalendarItem,
    day,
    windowEvent,
  );

  const el = windowEvent.target as HTMLElement;

  el.classList.remove('draghover');
}

//#region Calendar Items

function itemComparer(a: NormalizedCalendarItem, b: NormalizedCalendarItem) {
  if (a.startDate < b.startDate) return -1;
  if (b.startDate < a.startDate) return 1;
  if (a.endDate > b.endDate) return -1;
  if (b.endDate > a.endDate) return 1;

  return a.id < b.id ? -1 : 1;
}

/**
 * Return a list of items that INCLUDE any portion of a given week.
 */
function findAndSortItemsInWeek(weekStart: Date) {
  return findAndSortItemsInDateRange(weekStart, math.addDays(weekStart, 6));
}

/**
 * Return a list of items that INCLUDE any day within the date range, inclusive,
 * sorted so items that start earlier are returned first.
 */
function findAndSortItemsInDateRange(startDate: Date, endDate: Date) {
  return fixedItems.value
    .filter((item) => {
      return (
        item.endDate >= startDate && math.dateOnly(item.startDate) <= endDate
      );
    })
    .sort(itemComparer);
}

/**
 * Return a list of items that CONTAIN the week starting on a day. Sorted so the
 * items that start earlier are always shown first.
 *
 * @param weekStart ...
 * @returns ...
 */
function getWeekItems(weekStart: Date) {
  const items = findAndSortItemsInWeek(weekStart);

  const results = [] as NormalizedCalendarItem[];

  const itemRows: boolean[][] = [[], [], [], [], [], [], []];

  if (!items) return results;

  for (const item of items) {
    const ep = Object.assign({}, item, {
      classes: [...(item?.classes ?? [])],
      itemRow: 0,
    });

    const continued = ep.startDate < weekStart;
    const startOffset = continued ? 0 : math.dayDiff(weekStart, ep.startDate);

    const span = Math.max(
      1,
      Math.min(
        7 - startOffset,
        math.dayDiff(math.addDays(weekStart, startOffset), ep.endDate) + 1,
      ),
    );

    if (continued) {
      ep.classes.push('continued');
    }

    if (math.dayDiff(weekStart, ep.endDate) > 6) {
      ep.classes.push('to-be-continued');
    }

    if (math.isInPast(ep.endDate)) {
      ep.classes.push('past');
    }

    if (ep.originalItem.url) {
      ep.classes.push('has-url');
    }

    for (let d = 0; d < 7; d++) {
      const row = itemRows[d] as boolean[];

      if (d === startOffset) {
        let s = 0;

        while (row[s]) s++;

        ep.itemRow = s;
        row[s] = true;
      } else if (d < startOffset + span) {
        row[ep.itemRow] = true;
      }
    }

    ep.classes.push(`item-offset-${startOffset}`);
    ep.classes.push(`item-span-${span}`);

    results.push(ep);
  }

  return results;
}

/**
 * Compute the top position of the item based on its assigned row within the
 * given week.
 */
function getItemTop(item: NormalizedCalendarItem) {
  const r = item.itemRow;
  const h = props.itemContentHeight;
  const b = props.itemBorderHeight;

  return `calc(${props.itemTop} + ${r}*${h} + ${r}*${b})`;
}

//#endregion Calendar Items

const weekdays = computed(() => {
  const names = math.getFormattedWeekdayNames(
    displayLocale.value,
    'long',
    props.startingDayOfWeek,
  );

  return names.map((name, index) => ({
    index,
    key: name.toLowerCase().replaceAll(' ', '-'),
    label: weekdayNames.value[index] as string,
  }));
});

const dayHeaderItems = computed(() => {
  return weekdays.value.map((weekday) => ({
    key: getColumnDOWClass(weekday.index),
    label: weekday.label,
  }));
});

const calendarDayParams = useCalendarDayParams(calendar);

provide('calendarDayParams', calendarDayParams);

const { maxDisplayable: maxDisplayableCalendarItems } = calendarDayParams;

const weekItems = computed(() => {
  return weeksOfPeriod.value.map((weekStart, index) => {
    // Get the items for the week, and compute the top position of each item.
    const items = getWeekItems(weekStart).map((item) => {
      return { item, top: getItemTop(item) };
    });

    const days = math.daysOfWeek(weekStart).map((date, index) => {
      const dow = getDOW(index);

      // For each day, determine the number of calendar items that would otherwise
      // be displayed provided enough vertical space exists, then compute if,
      // and how many, calendar items would need to be hidden.

      let itemCount = 0;

      for (const { item } of items) {
        const startDate = getNormalizedDate(item.startDate);
        const endDate = getNormalizedDate(item.endDate);

        if (startDate <= date && endDate >= date) itemCount++;
      }

      return {
        index,
        dow,
        date,
        itemCount,
      } as DayProps;
    });

    return {
      key: `${index}-week`,
      numberInPeriod: index + 1,
      numberInYear: periodStartCalendarWeek.value + index,
      startDate: weekStart,
      isoYearMonthDay: math.isoYearMonthDay(weekStart),
      days,
      items,
    };
  });
});

const displayedDays = computed(() => {
  return weekItems.value.flatMap((week) => week.days.map(({ date }) => date));
});

expose({
  displayedDays,
});

//#region Helper Functions

function getNormalizedDate(date: string | Date) {
  const dateString = typeof date === 'string' ? date : date.toISOString();

  return new Date((dateString.split('T').at(0) as string) + 'T00:00:00.000');
}

//#endregion Helper Functions

return { __sfc: true,props, emit, calendar, currentDragItem, dateSelectionOrigin, displayLocale, periodStart, periodEnd, periodStartCalendarWeek, displayFirstDate, displayLastDate, weeksOfPeriod, monthNames, weekdayNames, fixedItems, currentPeriodStart, periodLabel, currentPeriodLabelFinal, state, headerProps, periodRange, classList, periodRangeKey, onClickDay, onClickItem, getDOW, getColumnDOWClass, getIncrementedPeriod, onMouseEnterItem, onMouseLeaveItem, onDragDateStart, onDragItemStart, onResizeItemStart, getSelectedDateRange, onDragOver, onDragEnter, onDragLeave, onDrop, itemComparer, findAndSortItemsInWeek, findAndSortItemsInDateRange, getWeekItems, getItemTop, weekdays, dayHeaderItems, calendarDayParams, maxDisplayableCalendarItems, weekItems, displayedDays, getNormalizedDate, Item, Day }
}

})